import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { forwardRef, useCallback, useRef, useEffect } from 'react';
import { cn } from '../../utils/cn';
import { useTheme } from '../../contexts/theme-context';
export const TextArea = forwardRef(({ label, error, helperText, size = 'md', required, fullWidth = true, rows = 3, minRows, maxRows, autoResize = false, showCharCount = false, maxLength, resize = 'vertical', className, wrapperClassName, labelClassName, disabled, id, value, onChange, ...props }, ref) => {
    const { theme } = useTheme();
    const isCompact = theme?.layoutDensity === 'compact';
    const inputId = id || React.useId();
    const internalRef = useRef(null);
    const mergedRef = ref || internalRef;
    const characterCount = typeof value === 'string' ? value.length : 0;
    // Auto-resize handler
    const adjustHeight = useCallback(() => {
        if (!autoResize || !mergedRef.current)
            return;
        const textarea = mergedRef.current;
        textarea.style.height = 'auto';
        const computedStyle = window.getComputedStyle(textarea);
        const lineHeight = parseInt(computedStyle.lineHeight) || 20;
        const paddingTop = parseInt(computedStyle.paddingTop) || 0;
        const paddingBottom = parseInt(computedStyle.paddingBottom) || 0;
        const borderTop = parseInt(computedStyle.borderTopWidth) || 0;
        const borderBottom = parseInt(computedStyle.borderBottomWidth) || 0;
        const minHeight = minRows
            ? lineHeight * minRows + paddingTop + paddingBottom + borderTop + borderBottom
            : undefined;
        const maxHeight = maxRows
            ? lineHeight * maxRows + paddingTop + paddingBottom + borderTop + borderBottom
            : undefined;
        let newHeight = textarea.scrollHeight;
        if (minHeight && newHeight < minHeight) {
            newHeight = minHeight;
        }
        if (maxHeight && newHeight > maxHeight) {
            newHeight = maxHeight;
            textarea.style.overflowY = 'auto';
        }
        else {
            textarea.style.overflowY = 'hidden';
        }
        textarea.style.height = `${newHeight}px`;
    }, [autoResize, minRows, maxRows, mergedRef]);
    // Adjust height on value change
    useEffect(() => {
        adjustHeight();
    }, [value, adjustHeight]);
    // Adjust height on mount
    useEffect(() => {
        if (autoResize) {
            adjustHeight();
        }
    }, [autoResize, adjustHeight]);
    const handleChange = useCallback((e) => {
        onChange?.(e);
        if (autoResize) {
            adjustHeight();
        }
    }, [onChange, autoResize, adjustHeight]);
    // Size classes
    const sizeClasses = {
        sm: isCompact ? 'text-xs px-2 py-1.5' : 'text-sm px-2.5 py-2',
        md: isCompact ? 'text-sm px-2.5 py-2' : 'text-sm px-3 py-2.5',
        lg: isCompact ? 'text-base px-3 py-2.5' : 'text-base px-4 py-3',
    };
    const labelSizeClasses = {
        sm: isCompact ? 'text-xs' : 'text-xs',
        md: isCompact ? 'text-xs' : 'text-sm',
        lg: isCompact ? 'text-sm' : 'text-base',
    };
    const resizeClasses = {
        none: 'resize-none',
        vertical: 'resize-y',
        horizontal: 'resize-x',
        both: 'resize',
    };
    return (_jsxs("div", { className: cn('space-y-1.5', fullWidth && 'w-full', wrapperClassName), children: [_jsxs("div", { className: "flex items-center justify-between", children: [label && (_jsxs("label", { htmlFor: inputId, className: cn('block font-medium text-gray-700 dark:text-gray-200', labelSizeClasses[size], labelClassName), children: [label, required && _jsx("span", { className: "text-red-500 ml-1", children: "*" })] })), showCharCount && (_jsxs("span", { className: cn('text-gray-400 dark:text-gray-500', isCompact ? 'text-xs' : 'text-sm'), children: [characterCount, maxLength ? `/${maxLength}` : ''] }))] }), _jsx("textarea", { ref: mergedRef, id: inputId, rows: rows, disabled: disabled, required: required, value: value, onChange: handleChange, maxLength: maxLength, className: cn(
                // Base styles
                'w-full rounded-md border bg-white dark:bg-gray-800 transition-all duration-200', 'placeholder:text-gray-400 dark:placeholder:text-gray-500', 'text-gray-900 dark:text-white', 
                // Focus styles
                'focus:outline-none focus:ring-2 focus:ring-offset-0', 
                // Size
                sizeClasses[size], 
                // Resize
                autoResize ? 'resize-none overflow-hidden' : resizeClasses[resize], 
                // States
                error
                    ? 'border-red-500 dark:border-red-400 focus:ring-red-500 dark:focus:ring-red-400 focus:border-red-500'
                    : 'border-gray-300 dark:border-gray-600 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400', 
                // Disabled
                disabled && 'opacity-50 cursor-not-allowed bg-gray-50 dark:bg-gray-700', 
                // Hover
                !disabled && !error && 'hover:border-gray-400 dark:hover:border-gray-500', className), ...props }), error && (_jsx("p", { className: cn('text-red-600 dark:text-red-400', isCompact ? 'text-xs' : 'text-sm'), children: error })), helperText && !error && (_jsx("p", { className: cn('text-gray-500 dark:text-gray-400', isCompact ? 'text-xs' : 'text-sm'), children: helperText }))] }));
});
TextArea.displayName = 'TextArea';
